import { useEffect, useRef, useCallback } from 'react';

const IDLE_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

/**
 * Logs out after idle period. Optional warning callback fires `warnMs` before expiry.
 */
export function useIdleTimeout({ enabled, timeoutMs, warnMs, onWarn, onTimeout }) {
    const timerRef = useRef(null);
    const warnTimerRef = useRef(null);
    const onWarnRef = useRef(onWarn);
    const onTimeoutRef = useRef(onTimeout);

    onWarnRef.current = onWarn;
    onTimeoutRef.current = onTimeout;

    const clearTimers = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
        timerRef.current = null;
        warnTimerRef.current = null;
    }, []);

    const schedule = useCallback(() => {
        clearTimers();
        if (!enabled) return;

        if (warnMs > 0 && warnMs < timeoutMs) {
            warnTimerRef.current = setTimeout(() => {
                onWarnRef.current?.();
            }, timeoutMs - warnMs);
        }

        timerRef.current = setTimeout(() => {
            onTimeoutRef.current?.();
        }, timeoutMs);
    }, [clearTimers, enabled, timeoutMs, warnMs]);

    useEffect(() => {
        if (!enabled) {
            clearTimers();
            return undefined;
        }

        const reset = () => schedule();
        IDLE_EVENTS.forEach((ev) => window.addEventListener(ev, reset, { passive: true }));
        schedule();

        return () => {
            clearTimers();
            IDLE_EVENTS.forEach((ev) => window.removeEventListener(ev, reset));
        };
    }, [enabled, schedule, clearTimers]);
}
